import React, { useCallback } from "react";
import styled from "styled-components";
import { Box, Text, TextLink } from "@origin-digital/ods-core";

interface SkipLinkProps {
  /**
   * The target element to skip to:
   * - Microsite entry point `#oetal-application-root`
   * - Wordpress page content `#page`
   */
  target: "#oetal-application-root" | "#page";
}

const SkipLinkContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-height: 0;
  background: transparent;
  z-index: 1000;
  transition: max-height 0.3s ease;
  &:focus-within {
    max-height: 100px;
  }
`;

const SkipLinkBox = styled(Box)`
  margin: 0;
  border-radius: 0;
`;

const SkipLinkText = styled(Text)`
  margin: 0;
`;

const SkipLinkAnchor = styled(TextLink)`
  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 2px;
  }
`;

export const SkipLink = React.memo(({ target }: SkipLinkProps) => {
  const handleSkipLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const currentTarget = document.querySelector(target);
      if (currentTarget instanceof HTMLElement) {
        currentTarget.setAttribute("tabindex", "-1");
        currentTarget.focus();
        currentTarget.scrollIntoView({ behavior: "smooth" });
      } else {
        (event.currentTarget as HTMLElement).blur();
      }
    },
    [target]
  );

  return (
    <SkipLinkContainer>
      <SkipLinkBox
        backgroundColor="neutralDark"
        paddingX="medium"
        paddingY="small"
      >
        <SkipLinkText tone="white">
          <SkipLinkAnchor
            href={target}
            onClick={handleSkipLinkClick}
            data-id="skip-link"
            aria-label="Skip to main content"
          >
            Skip to content
          </SkipLinkAnchor>
        </SkipLinkText>
      </SkipLinkBox>
    </SkipLinkContainer>
  );
});
